import { SearchOutlined } from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { Button, Input, Typography } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { filterMenuTree, type MenuSearchNode } from './filterMenuItems';

type MenuSearchProps = {
  collapsed?: boolean;
  keyword: string;
  menuData?: MenuSearchNode[];
  onExpand?: () => void;
  onKeywordChange: (value: string) => void;
};

const MenuSearch: React.FC<MenuSearchProps> = ({
  collapsed = false,
  keyword,
  menuData,
  onExpand,
  onKeywordChange,
}) => {
  const intl = useIntl();
  const placeholder = intl.formatMessage({
    id: 'component.menuSearch.placeholder',
  });

  const hasQuery = keyword.trim().length > 0;
  const noMatches = useMemo(
    () => hasQuery && filterMenuTree(menuData, keyword).length === 0,
    [hasQuery, keyword, menuData],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onKeywordChange(event.target.value);
    },
    [onKeywordChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        onKeywordChange('');
      }
    },
    [onKeywordChange],
  );

  if (collapsed) {
    return (
      <div className="menu-search menu-search-collapsed">
        <Button
          type="text"
          aria-label={placeholder}
          icon={<SearchOutlined />}
          onClick={onExpand}
        />
      </div>
    );
  }

  return (
    <div className="menu-search">
      <Input
        allowClear
        autoComplete="off"
        aria-label={placeholder}
        placeholder={placeholder}
        prefix={<SearchOutlined />}
        value={keyword}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {noMatches ? (
        <Typography.Text className="menu-search-empty" type="secondary">
          {intl.formatMessage({ id: 'component.menuSearch.empty' })}
        </Typography.Text>
      ) : null}
    </div>
  );
};

export default MenuSearch;
